/**
 * <ClientTile> Component
 */
import PropTypes from 'prop-types';
/** @jsx jsx this comment is required for Storybook to render */
import { css, jsx } from '@emotion/core';
import {
  breakpoints,
  colors,
  durations,
  fontSizes,
  rgba,
  shadows,
  unitConverter as uc,
} from '../../../styles/base';
import { Heading3 } from '../../atoms/Headings/Headings';
import AnimatedButton from '../../atoms/Buttons/AnimatedButton/AnimatedButton';
import sanityImage from '../../../utils/sanityImage';
import Card from '../../atoms/Card/Card';
import Image from '../../atoms/Image/Image';
import VideoButton from '../../atoms/Buttons/VideoButton/VideoButton';
import TileContainer from '../TileContainer/TileContainer';

const ClientTileV1 = ({
  animatedButton,
  bgImage,
  clientEntity,
  heading,
  videoButton,
}) => {
  const clientTileCss = css`
    position: relative;
    height: ${uc('475px')};

    @media (${breakpoints.largeTablet}) {
      min-height: ${uc('520px')};
      font-size: ${fontSizes.twenty};
    }

    overflow: hidden;
    transition: ${durations.medium};

    @media (${breakpoints.smallDesktopMin}) {
      &:hover {
        box-shadow: ${shadows.largeHovered};

        .bg-image {
          clip-path: polygon(0 0, 100% 0, 100% 71%, 0 90%);
        }

        .client-logo {
          box-shadow: ${uc('0 0 10px 10px')} rgba(${rgba(colors.black, 0.1)});
          transform: scale(1.1) translateY(${uc('-20px')});
        }

        .hover-overlay {
          opacity: 0.4;
        }

        ${Heading3},
        .button-container {
          transform: translateY(${uc('-20px')});
        }
      }
    }
  `;

  const clientTileContainerCss = css`
    margin-bottom: ${uc('50px')};
  `;

  const backgroundCss = css`
    height: 60%;
    overflow: hidden;
    background-repeat: no-repeat;
    background-size: cover;
    border-top-left-radius: ${uc('50px')};
    border-top-right-radius: ${uc('50px')};
    transition: ${durations.medium};
    clip-path: polygon(0 0, 100% 0, 100% 81%, 0 100%);
  `;

  const hoverOverlayCss = css`
    position: absolute;
    width: 100%;
    height: 120%;
    background: ${clientEntity.clientLogoColor.asset &&
    clientEntity.clientLogoColor.asset.metadata.palette.vibrant
      ? clientEntity.clientLogoColor.asset.metadata.palette.vibrant.background
      : colors.lightGray.three};
    border-radius: ${uc('50px')};
    transform: rotate(0deg);
    transform-origin: bottom left;
    opacity: 0;
    transition: ${durations.medium};
  `;

  const imageCss = css`
    position: absolute;
    top: 30%;
    right: 15%;
    height: ${uc('130px')};
    border-radius: 50%;
    transform-origin: bottom right;
    transition: ${durations.medium};
    transition-delay: 20ms;
  `;

  const headingCss = css`
    display: block;
    margin: ${uc('25.2px 30px 32px 30px')};
    letter-spacing: ${uc('0.5px')};
    transition: ${durations.medium};
    transition-delay: 20ms;

    @media (${breakpoints.tablet}) {
      font-size: ${fontSizes.eighteen};
    }
  `;

  const buttonContainerCss = css`
    position: absolute;
    bottom: 0;
    display: flex;
    margin: ${uc('0 0 20px 30px')};
    transition: ${durations.medium};
    transition-delay: 40ms;
  `;

  const tileBgImage = {
    backgroundImage: `url(${sanityImage(bgImage)
      .width(450)
      .fit('max')
      .format('jpg')
      .url()})`,
  };

  return (
    <TileContainer css={clientTileContainerCss}>
      <Card css={clientTileCss}>
        <div css={backgroundCss} style={tileBgImage} className="bg-image">
          <div css={hoverOverlayCss} className="hover-overlay" />
        </div>
        {clientEntity.clientLogoColor && (
          <Image
            className="client-logo"
            css={imageCss}
            src={sanityImage(clientEntity.clientLogoColor)
              .width(450)
              .url()}
            alt={clientEntity.clientLogoColor.alt}
          />
        )}
        <Heading3
          css={headingCss}
          fontSize={fontSizes.twenty}
          color={colors.darkGray.two}
        >
          {heading}
        </Heading3>
        <div css={buttonContainerCss} className="button-container">
          {videoButton && (
            <VideoButton className="video-button" {...videoButton}>
              {videoButton.buttonText}
            </VideoButton>
          )}
          {animatedButton && (
            <AnimatedButton {...animatedButton}>
              {animatedButton.link && animatedButton.link.text}
            </AnimatedButton>
          )}
        </div>
      </Card>
    </TileContainer>
  );
};

ClientTileV1.propTypes = {
  heading: PropTypes.string,
  clientEntity: PropTypes.shape({
    clientLogoColor: PropTypes.shape({ asset: {}, alt: PropTypes.string }),
  }),
  clientLogo: PropTypes.shape({}),
  bgImage: PropTypes.shape({}),
  animatedButton: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool]),
  videoButton: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.bool]),
};

ClientTileV1.defaultProps = {
  heading: 'Client tile heading',
  clientEntity: {},
  clientLogo: {},
  bgImage: {},
  animatedButton: false,
  videoButton: false,
};

export default ClientTileV1;
